import React, { Component, Fragment } from "react";
import { Table, Button, Select, Tabs, Card, Row, Col, Tag, Tooltip, message, Popconfirm, Alert, DatePicker, Radio, Modal } from "antd";
import moment from "moment";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { EyeOutlined, ReloadOutlined, ExclamationCircleOutlined, CheckCircleOutlined, FilterOutlined, MoreOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  listDailySettlementsAPI,
  listMonthlySettlementsAPI,
  recalculateSettlementAPI,
  getCronStatusAPI,
  triggerDailyCronAPI,
} from "../../Graphs/Report/subAdminSettlementAPI";
import { getCityListAPI } from "../../Graphs/Master/City/getCityListAPI";
import { formatePrice } from "../../utils/general";
import { alertError } from "../../utils/alert";
import SettlementDetailsModal from "./SettlementDetailsModal";
import PayableBreakdownModal from "./PayableBreakdownModal";
import { canAccessSettlementAudit } from "../../constant/menu";
import "./SubAdminSettlementReport.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

export class SubAdminSettlementReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "daily",
      isLoading: false,
      cityData: [],
      // Monthly Tab State
      monthlyList: [],
      monthlyYear: moment().format("YYYY"),
      monthlyCity: "",
      monthlyStatus: "",
      monthlyKpis: {},
      monthlyPagination: { current: 1, pageSize: 12, total: 0 },
      // Daily Tab State
      dailyList: [],
      dailyStartDate: moment().subtract(14, "days").format("YYYY-MM-DD"),
      dailyEndDate: moment().format("YYYY-MM-DD"),
      dailyCity: "",
      dailyStatus: "",
      dailyFlaggedOnly: false,
      dailyKpis: {},
      dailyPagination: { current: 1, pageSize: 20, total: 0 },
      // Details Modal
      selectedSettlementId: null,
      isModalOpen: false,
      isAuditHelpOpen: false,
      // Breakdown Modal
      isBreakdownModalOpen: false,
      breakdownSettlementId: null,
      breakdownSettlementRecord: null,
      breakdownKpis: null,
      breakdownTitleContext: "",
      // Cron Status State
      cronStatus: null,
      isCronRunning: false,
    };
  }

  openBreakdownModal = (settlementId = null, kpis = null, record = null, titleContext = "") => {
    this.setState({
      isBreakdownModalOpen: true,
      breakdownSettlementId: settlementId,
      breakdownSettlementRecord: record,
      breakdownKpis: kpis,
      breakdownTitleContext: titleContext,
    });
  };

  closeBreakdownModal = () => {
    this.setState({
      isBreakdownModalOpen: false,
      breakdownSettlementId: null,
      breakdownSettlementRecord: null,
      breakdownKpis: null,
      breakdownTitleContext: "",
    });
  };

  componentDidMount() {
    if (!canAccessSettlementAudit(this.props.loginUserData)) {
      return;
    }
    this.fetchCityList();
    this.fetchMonthlySettlements(1);
    this.fetchDailySettlements(1);
    this.fetchCronStatus();
  }

  fetchCronStatus = async () => {
    try {
      const { authToken } = this.props;
      const res = await getCronStatusAPI(authToken);
      if (res && res.status === 200 && res.data) {
        this.setState({ cronStatus: res.data });
      }
    } catch (err) {
      // Silently fail without interrupting report UI
    }
  };

  handleTriggerDailyCron = async () => {
    try {
      this.setState({ isCronRunning: true });
      const { authToken } = this.props;
      const res = await triggerDailyCronAPI(authToken);
      if (res && res.status === 200) {
        message.success("Daily settlement cron executed successfully!");
        this.fetchCronStatus();
        this.fetchDailySettlements(1);
      } else {
        throw new Error((res && res.message) || "Failed to trigger cron");
      }
    } catch (err) {
      alertError("TRIGGER CRON", err);
    } finally {
      this.setState({ isCronRunning: false });
    }
  };

  fetchCityList = async () => {
    try {
      const { authToken, loginUserData } = this.props;
      const res = await getCityListAPI(authToken);

      if (res && res.status === 200) {
        let options = res.data.map((city) => ({
          label: city.name,
          value: city.id,
        }));

        if (loginUserData.role === "Admin") {
          options.unshift({ label: "all cities", value: "" });
        } else if (loginUserData.city) {
          options = options.filter((c) => c.value === loginUserData.city);
        }

        this.setState({
          cityData: options,
          monthlyCity: loginUserData.city || "",
          dailyCity: loginUserData.city || "",
        });
      }
    } catch (err) {
      alertError("CITY LIST", err);
    }
  };

  fetchMonthlySettlements = async (page = 1) => {
    try {
      this.setState({ isLoading: true });
      const { authToken, loginUserData } = this.props;
      const { monthlyYear, monthlyCity, monthlyStatus, monthlyPagination } = this.state;

      const city = loginUserData.role === "Sub_admin" ? loginUserData.city : monthlyCity;

      const payload = {
        year: monthlyYear,
        city: city || undefined,
        status: monthlyStatus || undefined,
        page,
        limit: monthlyPagination.pageSize,
      };

      const res = await listMonthlySettlementsAPI(authToken, payload);

      if (res && res.status === 200 && res.data) {
        this.setState({
          monthlyList: res.data.list || [],
          monthlyKpis: res.data.kpis || {},
          monthlyPagination: {
            ...monthlyPagination,
            current: page,
            total: (res.data.pagination && res.data.pagination.total) || 0,
          },
        });
      } else {
        throw new Error((res && res.message) || "Failed to fetch monthly settlements");
      }
    } catch (err) {
      alertError("MONTHLY SETTLEMENT", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  fetchDailySettlements = async (page = 1) => {
    try {
      this.setState({ isLoading: true });
      const { authToken, loginUserData } = this.props;
      const { dailyStartDate, dailyEndDate, dailyCity, dailyStatus, dailyFlaggedOnly, dailyPagination } = this.state;

      const city = loginUserData.role === "Sub_admin" ? loginUserData.city : dailyCity;

      const payload = {
        startDate: dailyStartDate,
        endDate: dailyEndDate,
        city: city || undefined,
        status: dailyStatus || undefined,
        is_flagged: dailyFlaggedOnly ? true : undefined,
        page,
        limit: dailyPagination.pageSize,
      };

      const res = await listDailySettlementsAPI(authToken, payload);

      if (res && res.status === 200 && res.data) {
        this.setState({
          dailyList: res.data.list || [],
          dailyKpis: res.data.kpis || {},
          dailyPagination: {
            ...dailyPagination,
            current: page,
            total: (res.data.pagination && res.data.pagination.total) || 0,
          },
        });
      } else {
        throw new Error((res && res.message) || "Failed to fetch daily settlements");
      }
    } catch (err) {
      alertError("DAILY SETTLEMENT", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleRecalculate = async (record) => {
    try {
      this.setState({ isLoading: true });
      const { authToken } = this.props;
      const res = await recalculateSettlementAPI(authToken, {
        city: record.city,
        periodType: record.period_type,
        periodDate: record.period_date,
        forceRecalculate: true,
      });

      if (res && res.status === 200) {
        message.success(`Recalculated settlement for ${record.city} successfully!`);
        if (record.period_type === "monthly") {
          this.fetchMonthlySettlements(this.state.monthlyPagination.current);
        } else {
          this.fetchDailySettlements(this.state.dailyPagination.current);
        }
      } else {
        throw new Error((res && res.message) || "Failed to recalculate");
      }
    } catch (err) {
      alertError("RECALCULATE", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  openDetails = (settlementId) => {
    this.setState({ selectedSettlementId: settlementId, isModalOpen: true });
  };

  closeDetails = () => {
    this.setState({ selectedSettlementId: null, isModalOpen: false });
  };

  onSettledSuccess = () => {
    this.fetchMonthlySettlements(this.state.monthlyPagination.current);
  };

  refreshActiveReport = () => {
    if (this.state.activeTab === "monthly") {
      this.fetchMonthlySettlements(this.state.monthlyPagination.current);
      return;
    }
    this.fetchDailySettlements(this.state.dailyPagination.current);
  };

  resetMonthlyFilters = () => {
    const { loginUserData } = this.props;
    this.setState(
      {
        monthlyYear: moment().format("YYYY"),
        monthlyCity: loginUserData.role === "Sub_admin" ? loginUserData.city || "" : "",
        monthlyStatus: "",
      },
      () => this.fetchMonthlySettlements(1)
    );
  };

  resetDailyFilters = () => {
    const { loginUserData } = this.props;
    this.setState(
      {
        dailyStartDate: moment().subtract(14, "days").format("YYYY-MM-DD"),
        dailyEndDate: moment().format("YYYY-MM-DD"),
        dailyCity: loginUserData.role === "Sub_admin" ? loginUserData.city || "" : "",
        dailyStatus: "",
        dailyFlaggedOnly: false,
      },
      () => this.fetchDailySettlements(1)
    );
  };

  renderMonthlyFilters = (isAdmin) => {
    const { cityData, monthlyYear, monthlyCity, monthlyStatus, isLoading } = this.state;

    return (
      <div className="settlement-filter-panel">
        <div className="settlement-filter-group">
          <Select
            className="settlement-filter-select year-select"
            value={monthlyYear}
            onChange={(val) => this.setState({ monthlyYear: val })}
            placeholder="Year"
          >
            <Option value="2027">2027</Option>
            <Option value="2026">2026</Option>
            <Option value="2025">2025</Option>
            <Option value="2024">2024</Option>
          </Select>
          {isAdmin && (
            <Select
              className="settlement-filter-select city-select"
              value={monthlyCity}
              onChange={(val) => this.setState({ monthlyCity: val })}
              placeholder="All Cities"
            >
              {cityData.map((city) => (
                <Option key={city.value} value={city.value}>
                  {city.label}
                </Option>
              ))}
            </Select>
          )}
          <Select
            className="settlement-filter-select status-select"
            value={monthlyStatus}
            onChange={(val) => this.setState({ monthlyStatus: val })}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value="pending">Pending</Option>
            <Option value="verified">Verified</Option>
            <Option value="settled">Settled</Option>
          </Select>
          <div className="settlement-filter-actions">
            <Button
              className="settlement-btn-apply"
              icon={<FilterOutlined />}
              loading={isLoading}
              onClick={() => this.fetchMonthlySettlements(1)}
            >
              Apply Filters
            </Button>
            <Button
              type="text"
              className="settlement-btn-reset"
              onClick={this.resetMonthlyFilters}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderDailyFilters = (isAdmin) => {
    const {
      cityData,
      dailyStartDate,
      dailyEndDate,
      dailyCity,
      dailyStatus,
      dailyFlaggedOnly,
      isLoading,
    } = this.state;

    return (
      <div className="settlement-filter-panel">
        <div className="settlement-filter-group">
          <RangePicker
            className="settlement-filter-date"
            placeholder={["Start Date", "End Date"]}
            value={[
              dailyStartDate ? moment(dailyStartDate) : null,
              dailyEndDate ? moment(dailyEndDate) : null,
            ]}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                this.setState({
                  dailyStartDate: dates[0].format("YYYY-MM-DD"),
                  dailyEndDate: dates[1].format("YYYY-MM-DD"),
                });
              } else {
                this.setState({ dailyStartDate: "", dailyEndDate: "" });
              }
            }}
          />
          {isAdmin && (
            <Select
              className="settlement-filter-select city-select"
              value={dailyCity}
              onChange={(val) => this.setState({ dailyCity: val })}
              placeholder="All Cities"
            >
              {cityData.map((city) => (
                <Option key={city.value} value={city.value}>
                  {city.label}
                </Option>
              ))}
            </Select>
          )}
          <Select
            className="settlement-filter-select status-select"
            value={dailyStatus}
            onChange={(val) => this.setState({ dailyStatus: val })}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value="pending">Pending</Option>
            <Option value="verified">Verified</Option>
            <Option value="settled">Settled</Option>
          </Select>
          <Select
            className="settlement-filter-select audit-select"
            value={dailyFlaggedOnly ? "flagged" : "all"}
            onChange={(val) => this.setState({ dailyFlaggedOnly: val === "flagged" })}
            placeholder="All Records"
          >
            <Option value="all">All Records</Option>
            <Option value="flagged">Flagged Only</Option>
          </Select>
          <div className="settlement-filter-actions">
            <Button
              className="settlement-btn-apply"
              icon={<FilterOutlined />}
              loading={isLoading}
              onClick={() => this.fetchDailySettlements(1)}
            >
              Apply Filters
            </Button>
            <Button
              type="text"
              className="settlement-btn-reset"
              onClick={this.resetDailyFilters}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>
    );
  };

  monthlyColumns = [
    {
      title: "Period",
      dataIndex: "period_label",
      key: "period_label",
      width: 100,
      render: (val) => <span className="settlement-period-badge">{val}</span>,
    },
    {
      title: "Branch / Sub-Admin",
      key: "branch",
      width: 180,
      render: (text, record) => {
        const cityName = (record.user_city && record.user_city.name) || record.city;
        const subAdminName =
          record.sub_admin &&
          `${record.sub_admin.first_name} ${record.sub_admin.last_name || ""}`.trim();
        return (
          <div className="settlement-table-primary">
            <span className="settlement-cell-title">{cityName}</span>
            {subAdminName ? (
              <span className="settlement-cell-sub">{subAdminName}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Total Sell",
      dataIndex: "total_retail_sales",
      key: "total_retail_sales",
      width: 140,
      align: "right",
      render: (val, record) => (
        <div className="settlement-table-metric">
          <span className="settlement-cell-title">{formatePrice(val)} LYD</span>
          <span className="settlement-cell-sub">{record.total_cards_count || 0} cards</span>
        </div>
      ),
    },
    {
      title: "From admin",
      dataIndex: "net_payable_to_admin",
      key: "net_payable_to_admin",
      width: 170,
      align: "right",
      render: (val, record) => {
        const payableCards = Number(record.borrowed_cards_count || 0) + Number(record.api_cards_count || 0);
        return (
          <div className="settlement-cell-payable-container">
            <div className="settlement-table-metric">
              <span className="settlement-cell-title settlement-payable">{formatePrice(val)} LYD</span>
              <span className="settlement-cell-sub">{payableCards.toLocaleString()} cards</span>
            </div>
            <Tooltip title="View breakdown (Admin, Mint API, Like API)">
              <PayableBreakdownModal
                settlementId={record.id}
                settlement={record}
                authToken={this.props.authToken}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  size="small"
                  className="settlement-table-icon-btn settlement-table-icon-btn--payable"
                  icon={<InfoCircleOutlined />}
                  aria-label="View breakdown"
                />
              </PayableBreakdownModal>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Payment",
      dataIndex: "status",
      key: "status",
      width: 105,
      align: "center",
      render: (status) => {
        let color = "orange";
        let label = "Pending";
        if (status === "settled") {
          color = "green";
          label = "Settled";
        } else if (status === "verified") {
          color = "blue";
          label = "Verified";
        }
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: (
        <span className="settlement-th-with-hint">
          Audit{" "}
          <Tooltip title="How is Verified calculated?">
            <InfoCircleOutlined
              className="settlement-th-hint-icon"
              onClick={(e) => {
                e.stopPropagation();
                this.setState({ isAuditHelpOpen: true });
              }}
            />
          </Tooltip>
        </span>
      ),
      key: "audit",
      width: 120,
      align: "center",
      render: (text, record) =>
        record.is_flagged ? (
          <Tooltip title={`${record.flag_count} discrepancy flag(s) detected. Click Details to view.`}>
            <Tag color="volcano" icon={<ExclamationCircleOutlined />}>
              Flagged ({record.flag_count})
            </Tag>
          </Tooltip>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Verified
          </Tag>
        ),
    },
    {
      title: "Actions",
      key: "action",
      width: 110,
      align: "center",
      render: (text, record) => {
        const isAdmin = Boolean(this.props.loginUserData && this.props.loginUserData.role === "Admin");
        return (
          <div className="settlement-actions">
            <div>
              <Tooltip placement="bottom" title="View">
                <Button
                  shape="circle"
                  icon={<EyeOutlined />}
                  onClick={() => this.openDetails(record.id)}
                />
              </Tooltip>
            </div>
            {isAdmin && (
              <div>
                <Tooltip placement="bottom" title="Recalculate">
                  <Popconfirm
                    title="Recalculate settlement for this month?"
                    onConfirm={() => this.handleRecalculate(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button shape="circle" icon={<ReloadOutlined />} />
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  dailyColumns = [
    {
      title: "Date",
      dataIndex: "period_date",
      key: "period_date",
      width: 110,
      render: (val) => <span className="settlement-period-badge">{val}</span>,
    },
    {
      title: "Branch City",
      key: "city",
      width: 130,
      render: (text, record) => (record.user_city && record.user_city.name) || record.city,
    },
    {
      title: "Total Sell",
      dataIndex: "total_retail_sales",
      key: "total_retail_sales",
      width: 140,
      align: "right",
      render: (val, record) => (
        <div className="settlement-table-metric">
          <span className="settlement-cell-title">{formatePrice(val)} LYD</span>
          <span className="settlement-cell-sub">{record.total_cards_count || 0} cards</span>
        </div>
      ),
    },
    {
      title: "From admin",
      dataIndex: "net_payable_to_admin",
      key: "net_payable_to_admin",
      width: 170,
      align: "right",
      render: (val, record) => {
        const payableCards = Number(record.borrowed_cards_count || 0) + Number(record.api_cards_count || 0);
        return (
          <div className="settlement-cell-payable-container">
            <div className="settlement-table-metric">
              <span className="settlement-cell-title settlement-payable">{formatePrice(val)} LYD</span>
              <span className="settlement-cell-sub">{payableCards.toLocaleString()} cards</span>
            </div>
            <Tooltip title="View breakdown (Admin, Mint API, Like API)">
              <PayableBreakdownModal
                settlementId={record.id}
                settlement={record}
                authToken={this.props.authToken}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  size="small"
                  className="settlement-table-icon-btn settlement-table-icon-btn--payable"
                  icon={<InfoCircleOutlined />}
                  aria-label="View breakdown"
                />
              </PayableBreakdownModal>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: (
        <span className="settlement-th-with-hint">
          Audit{" "}
          <Tooltip title="How is Verified calculated?">
            <InfoCircleOutlined
              className="settlement-th-hint-icon"
              onClick={(e) => {
                e.stopPropagation();
                this.setState({ isAuditHelpOpen: true });
              }}
            />
          </Tooltip>
        </span>
      ),
      key: "is_flagged",
      width: 115,
      align: "center",
      render: (text, record) =>
        record.is_flagged ? (
          <Tooltip title={`${record.flag_count} discrepancy flag(s) detected.`}>
            <Tag color="volcano">Flagged ({record.flag_count})</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">Clean</Tag>
        ),
    },
    {
      title: "Actions",
      key: "action",
      width: 110,
      align: "center",
      render: (text, record) => {
        const isAdmin = Boolean(this.props.loginUserData && this.props.loginUserData.role === "Admin");
        return (
          <div className="settlement-actions">
            <div>
              <Tooltip placement="bottom" title="View">
                <Button
                  shape="circle"
                  icon={<EyeOutlined />}
                  onClick={() => this.openDetails(record.id)}
                />
              </Tooltip>
            </div>
            {isAdmin && (
              <div>
                <Tooltip placement="bottom" title="Recalculate">
                  <Popconfirm
                    title="Recalculate settlement for this day?"
                    onConfirm={() => this.handleRecalculate(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button shape="circle" icon={<ReloadOutlined />} />
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  render() {
    const { loginUserData, authToken } = this.props;
    if (!canAccessSettlementAudit(loginUserData)) {
      return (
        <Fragment>
          <h5 style={{ marginTop: "30%", marginLeft: "30%" }}>
            You are not authorized for view this page.
          </h5>
        </Fragment>
      );
    }
    const isAdmin = Boolean(loginUserData && loginUserData.role === "Admin");
    const {
      activeTab,
      isLoading,
      monthlyList,
      monthlyKpis,
      monthlyPagination,
      dailyList,
      dailyKpis,
      dailyPagination,
      selectedSettlementId,
      isModalOpen,
      cronStatus,
      isCronRunning,
    } = this.state;

    const currentKpis = activeTab === "monthly" ? monthlyKpis : dailyKpis;
    const flaggedCount = currentKpis.total_flagged_count || 0;
    const cronState = (cronStatus && cronStatus.daily && cronStatus.daily.status) || "IDLE";
    const cronColor =
      cronState === "COMPLETED"
        ? "green"
        : cronState === "RUNNING"
        ? "blue"
        : cronState === "PARTIAL"
        ? "gold"
        : cronState === "FAILED"
        ? "volcano"
        : "default";

    return (
      <Fragment>
        <div className="settlement-audit-page">
          <div className="settlement-page-heading">
            <h3>Settlement Audit</h3>
            <div className="settlement-heading-actions">
              <Radio.Group
                className="settlement-period-switch"
                value={activeTab}
                onChange={(event) => this.setState({ activeTab: event.target.value })}
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
              <Button
                icon={<ReloadOutlined />}
                loading={isLoading}
                onClick={this.refreshActiveReport}
              >
                Refresh Data
              </Button>
            </div>
          </div>

          {activeTab === "monthly"
            ? this.renderMonthlyFilters(isAdmin)
            : this.renderDailyFilters(isAdmin)}

          <div className="settlement-kpi-grid">
            <div className="settlement-kpi-bar">
              <div className="settlement-kpi-item">
                <span className="settlement-kpi-label">Total Cards</span>
                <span className="settlement-kpi-value">
                  {currentKpis.total_cards_count || 0}
                </span>
                <span className="settlement-kpi-meta">
                  Total cards sold ({currentKpis.total_own_cards_count || 0} own)
                </span>
              </div>
              <div className="settlement-kpi-divider" />
              <div className="settlement-kpi-item settlement-kpi-item-sell">
                <span className="settlement-kpi-label">Sub-Admin Total Sell</span>
                <span className="settlement-kpi-value">
                  {formatePrice(currentKpis.total_retail_sales)} LYD
                </span>
              </div>
              <div className="settlement-kpi-divider" />
              <div className="settlement-kpi-item">
                <div className="settlement-kpi-label-row">
                  <span className="settlement-kpi-label">Amount Payable</span>
                  <Tooltip title="View breakdown (Admin, Mint API, Like API)">
                    <PayableBreakdownModal
                      kpis={currentKpis}
                      titleContext="All Branches Summary"
                      authToken={this.props.authToken}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        className="settlement-breakdown-icon-btn"
                        icon={<InfoCircleOutlined />}
                        aria-label="View breakdown"
                      />
                    </PayableBreakdownModal>
                  </Tooltip>
                </div>
                <span className="settlement-kpi-value">
                  {formatePrice(currentKpis.total_net_payable)} LYD
                </span>
                
              </div>
              <div className="settlement-kpi-divider" />
              <div
                className={`settlement-kpi-item settlement-kpi-audit ${
                  flaggedCount > 0 ? "has-flags" : "is-clean"
                }`}
              >
                <div className="settlement-kpi-label-row">
                  <span className="settlement-kpi-label">Audit Status</span>
                  <Tooltip title="How is Verified calculated?">
                    <Button
                      type="text"
                      size="small"
                      className="settlement-hint-btn"
                      icon={<InfoCircleOutlined />}
                      onClick={() => this.setState({ isAuditHelpOpen: true })}
                      aria-label="How is Verified calculated?"
                    />
                  </Tooltip>
                </div>
                <span className="settlement-kpi-value">
                  {flaggedCount > 0 ? (
                    <span className="audit-flagged">
                      <ExclamationCircleOutlined /> {flaggedCount} Flagged
                    </span>
                  ) : (
                    <span className="audit-verified">
                      <CheckCircleOutlined /> Verified
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <Card bordered={false} className="settlement-report-card">
            <Tabs
              className="settlement-tabs"
              activeKey={activeTab}
              onChange={(key) => this.setState({ activeTab: key })}
            >
              <TabPane tab="Monthly" key="monthly">
                <Table
                  className="settlement-table"
                  columns={this.monthlyColumns}
                  dataSource={monthlyList}
                  rowKey="id"
                  loading={isLoading}
                  pagination={{
                    current: monthlyPagination.current,
                    pageSize: monthlyPagination.pageSize,
                    total: monthlyPagination.total,
                    onChange: (page) => this.fetchMonthlySettlements(page),
                    showSizeChanger: false,
                    showTotal: (total) => `${total} statements`,
                  }}
                  size="middle"
                  scroll={{ x: 1040 }}
                  rowClassName={(record) => record.is_flagged ? "settlement-row-flagged" : ""}
                  locale={{
                    emptyText: (
                      <div className="settlement-empty-state">
                        <span className="settlement-empty-title">No Monthly Settlements Found</span>
                        <span>Try changing or resetting the filters.</span>
                        <Button type="link" onClick={this.resetMonthlyFilters}>Reset Filters</Button>
                      </div>
                    ),
                  }}
                />
              </TabPane>

              <TabPane tab="Daily" key="daily">
                {cronStatus && (
                  <div className="settlement-cron-status">
                    <div className="settlement-cron-summary">
                      <span className={`settlement-cron-dot status-${cronState.toLowerCase()}`} />
                      <span>
                        Daily Processing: {cronState.charAt(0).toUpperCase() + cronState.slice(1).toLowerCase()}
                      </span>
                      <span className="settlement-cron-divider">·</span>
                      <span>
                        {cronStatus.yesterdayPersistedCount || 0} Snapshots ({cronStatus.yesterdayDate})
                      </span>
                      {cronStatus.daily && cronStatus.daily.lastRunAt && (
                        <>
                          <span className="settlement-cron-divider">·</span>
                          <span className="settlement-cron-time">
                            Last Run {moment(cronStatus.daily.lastRunAt).format("DD MMM, HH:mm")}
                          </span>
                        </>
                      )}
                    </div>
                    {isAdmin && (
                      <Button
                        size="small"
                        type="link"
                        icon={<ReloadOutlined />}
                        loading={isCronRunning}
                        onClick={this.handleTriggerDailyCron}
                      >
                        Run Now
                      </Button>
                    )}
                  </div>
                )}

                {cronStatus && cronStatus.daily && cronStatus.daily.errors && cronStatus.daily.errors.length > 0 && (
                  <Alert
                    className="settlement-cron-errors"
                    type="warning"
                    showIcon
                    message={`${cronStatus.daily.errors.length} Processing Error(s) Require Attention`}
                    description={cronStatus.daily.errors.map((errItem, idx) => (
                      <div key={idx}><span>{errItem.city}:</span> {errItem.error}</div>
                    ))}
                  />
                )}

                <Table
                  className="settlement-table"
                  columns={this.dailyColumns}
                  dataSource={dailyList}
                  rowKey="id"
                  loading={isLoading}
                  pagination={{
                    current: dailyPagination.current,
                    pageSize: dailyPagination.pageSize,
                    total: dailyPagination.total,
                    onChange: (page) => this.fetchDailySettlements(page),
                    showSizeChanger: false,
                    showTotal: (total) => `${total} daily records`,
                  }}
                  size="middle"
                  scroll={{ x: 900 }}
                  rowClassName={(record) => record.is_flagged ? "settlement-row-flagged" : ""}
                  locale={{
                    emptyText: (
                      <div className="settlement-empty-state">
                        <span className="settlement-empty-title">No Daily Settlements Found</span>
                        <span>Try changing or resetting the filters.</span>
                        <Button type="link" onClick={this.resetDailyFilters}>Reset Filters</Button>
                      </div>
                    ),
                  }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </div>

        {/* Drilldown Modal */}
        <SettlementDetailsModal
          visible={isModalOpen}
          settlementId={selectedSettlementId}
          authToken={authToken}
          isAdmin={isAdmin}
          onClose={this.closeDetails}
          onSettledSuccess={this.onSettledSuccess}
        />

        {/* Verification Checks Hint Dialog */}
        <Modal
          wrapClassName="settlement-details-modal"
          title="Verification checks"
          visible={this.state.isAuditHelpOpen}
          onCancel={() => this.setState({ isAuditHelpOpen: false })}
          footer={[
            <Button
              key="close"
              type="primary"
              onClick={() => this.setState({ isAuditHelpOpen: false })}
            >
              Got it
            </Button>,
          ]}
          width={440}
        >
          <div className="settlement-hint-dialog">
            <p className="settlement-hint-intro">
              A settlement is marked <strong>Verified</strong> when all 4 checks pass:
            </p>
            <div className="settlement-hint-checks">
              <div className="settlement-hint-check-item">
                <div className="hint-check-title">1. Inventory conservation</div>
                <div className="hint-check-desc">
                  Every admin card sold has a claimed inventory record.
                </div>
              </div>
              <div className="settlement-hint-check-item">
                <div className="hint-check-title">2. Cost price integrity</div>
                <div className="hint-check-desc">
                  Every card sold has an explicit supplier purchase cost.
                </div>
              </div>
              <div className="settlement-hint-check-item">
                <div className="hint-check-title">3. Financial debit match</div>
                <div className="hint-check-desc">
                  Sales match customer wallet deductions (within 5 LYD or 5%).
                </div>
              </div>
              <div className="settlement-hint-check-item">
                <div className="hint-check-title">4. City allocation guard</div>
                <div className="hint-check-desc">
                  All branch transactions have an assigned city.
                </div>
              </div>
            </div>
            <div className="settlement-hint-summary">
              Any discrepancy marks the record as <strong>Flagged</strong>.
            </div>
          </div>
        </Modal>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken,
  loginUserData: state.auth.loginUserData,
});

export default connect(mapStateToProps)(withRouter(SubAdminSettlementReport));
